// src/components/Register.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faEnvelope, faPhone, faUser, faLock, faFileContract, faShieldAlt, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { useTranslation } from 'react-i18next';
import './AuthPage.css';

const API_BASE = 'https://api.insulated.pro';

const COUNTRIES = [
  { name: 'Bénin', code: '+229' },
  { name: 'Burkina Faso', code: '+226' },
  { name: 'Cameroun', code: '+237' },
  { name: 'Centrafrique', code: '+236' },
  { name: 'Congo (Rép. du Congo)', code: '+242' },
  { name: 'Côte d\'Ivoire', code: '+225' },
  { name: 'Djibouti', code: '+253' },
  { name: 'Gabon', code: '+241' },
  { name: 'Gambie', code: '+220' },
  { name: 'Ghana', code: '+233' },
  { name: 'Guinée', code: '+224' },
  { name: 'Guinée-Bissau', code: '+245' },
  { name: 'Guinée équatoriale', code: '+240' },
  { name: 'Kenya', code: '+254' },
  { name: 'Libéria', code: '+231' },
  { name: 'Mali', code: '+223' },
  { name: 'Niger', code: '+227' },
  { name: 'Nigeria', code: '+234' },
  { name: 'Pays-Bas', code: '+31' },
  { name: 'RD Congo', code: '+243' },
  { name: 'Sénégal', code: '+221' },
  { name: 'Sierra Leone', code: '+232' },
  { name: 'Soudan du Sud', code: '+211' },
  { name: 'Tchad', code: '+235' },
  { name: 'Togo', code: '+228' }
].sort((a, b) => a.name.localeCompare(b.name));

// 🆕 Utilitaire : génère ou récupère un identifiant unique d'appareil (Device Hash)
const getDeviceHash = () => {
  try {
    let hash = localStorage.getItem('device_hash');
    if (!hash) {
      hash = (crypto.randomUUID && crypto.randomUUID()) ||
             `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem('device_hash', hash);
    }
    return hash;
  } catch {
    return null;
  }
};

const Register = () => {
  const { t } = useTranslation(['auth', 'common']);
  const [method, setMethod] = useState('email');
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    countryCode: '+229',
    phoneNumber: '',
    password: '',
    password2: ''
  });
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  // 🆕 État pour les 2 cases obligatoires
  const [acceptedCGU, setAcceptedCGU] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  // 🆕 État pour la modale d'affichage des documents légaux
  const [legalModal, setLegalModal] = useState({ open: false, type: null, title: '', content: '', loading: false });

  // 🆕 Récupération des documents légaux depuis l'API
  const [legalDocs, setLegalDocs] = useState({ cgu: null, privacy: null });

  useEffect(() => {
    const fetchLegalDocs = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/auth/legal/current`);
        setLegalDocs({
          cgu: res.data?.cgu || null,
          privacy: res.data?.privacy || null
        });
      } catch (err) {
        console.warn('[LEGAL] Impossible de récupérer les documents légaux:', err.message);
      }
    };
    fetchLegalDocs();
  }, []);

  const openLegalModal = (type) => {
    const doc = type === 'cgu' ? legalDocs.cgu : legalDocs.privacy;
    setLegalModal({
      open: true,
      type,
      title: type === 'cgu' ? 'Conditions Générales d\'Utilisation' : 'Politique de Confidentialité',
      content: doc?.contentHtml || '<p>Document indisponible pour le moment.</p>',
      loading: false
    });
  };

  const closeLegalModal = () => setLegalModal({ open: false, type: null, title: '', content: '', loading: false });

  const { firstName, email, countryCode, phoneNumber, password, password2 } = formData;

  const onChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

  // 🆕 Le bouton est désactivé tant que les 2 cases ne sont pas cochées
  const isFormValid = acceptedCGU && acceptedPrivacy;

  const onSubmit = async e => {
    e.preventDefault();

    // 🆕 Double sécurité : on vérifie à nouveau côté client
    if (!acceptedCGU || !acceptedPrivacy) {
      setMessage('Vous devez accepter les CGU et la Politique de Confidentialité pour créer un compte.');
      return;
    }

    if (password !== password2) {
      setMessage(t('auth:validation.passwords_mismatch'));
      return;
    }

    try {
      const config = { headers: { 'Content-Type': 'application/json' } };

      const body = {
        firstName,
        password,
        // 🆕 Ajout des 2 consentements obligatoires
        acceptedCGU: true,
        acceptedPrivacy: true,
        deviceHash: getDeviceHash()
      };

      if (method === 'email') {
        body.email = email;
      } else {
        body.phoneNumber = `${countryCode}${phoneNumber}`;
      }

      const res = await axios.post(
        `${API_BASE}/api/auth/register`,
        JSON.stringify(body),
        config
      );
      localStorage.setItem('token', res.data.token);

      // 🆕 PHASE THEME : notifier le ThemeContext du changement d'utilisateur
      window.dispatchEvent(new Event('user:changed'));

      navigate('/dashboard');
    } catch (err) {
      console.error(err.response?.data || err);
      // 🆕 Gestion des erreurs de validation renvoyées par le backend
      const backendErrors = err.response?.data?.errors;
      if (Array.isArray(backendErrors) && backendErrors.length > 0) {
        setMessage(backendErrors[0].msg);
      } else {
        setMessage(err.response?.data?.message || t('common:errors.generic'));
      }
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE}/api/auth/google`;
  };

  return (
    <div className="auth-page">
      <div className="auth-orb auth-orb-1"></div>
      <div className="auth-orb auth-orb-2"></div>
      <div className="auth-orb auth-orb-3"></div>

      <div className="auth-card">
        <div className="auth-brand">
          <div className="auth-brand-icon">🚀</div>
          <span className="auth-brand-name">INSULATED</span>
        </div>

        <h1 className="auth-title">{t('auth:register.title')}</h1>
        <p className="auth-subtitle">
          {t('auth:register.subtitle_platform')}
        </p>

        {message && (
          <div className="auth-alert auth-alert-error">{message}</div>
        )}

        <form onSubmit={onSubmit}>
          {/* Prénom */}
          <div className="auth-form-group">
            <FontAwesomeIcon icon={faUser} className="auth-input-icon" />
            <input
              type="text"
              className="auth-input"
              placeholder={t('auth:register.first_name_placeholder')}
              name="firstName"
              value={firstName}
              onChange={onChange}
              required
            />
          </div>

          {/* Toggle Email / Téléphone */}
          <div className="auth-toggle">
            <button
              type="button"
              className={`auth-toggle-btn ${method === 'email' ? 'active' : ''}`}
              onClick={() => setMethod('email')}
            >
              <FontAwesomeIcon icon={faEnvelope} />
              {t('auth:register.toggle_email')}
            </button>
            <button
              type="button"
              className={`auth-toggle-btn ${method === 'phone' ? 'active' : ''}`}
              onClick={() => setMethod('phone')}
            >
              <FontAwesomeIcon icon={faPhone} />
              {t('auth:register.toggle_phone')}
            </button>
          </div>

          {/* Champ Email OU Téléphone */}
          {method === 'email' ? (
            <div className="auth-form-group">
              <FontAwesomeIcon icon={faEnvelope} className="auth-input-icon" />
              <input
                type="email"
                className="auth-input"
                placeholder={t('auth:register.email_placeholder')}
                name="email"
                value={email}
                onChange={onChange}
                required
              />
            </div>
          ) : (
            <div className="auth-phone-group">
              <select
                className="auth-select"
                name="countryCode"
                value={countryCode}
                onChange={onChange}
              >
                {COUNTRIES.map(c => (
                  <option key={c.code} value={c.code}>{c.code}</option>
                ))}
              </select>
              <div className="auth-input-wrapper">
                <FontAwesomeIcon icon={faPhone} className="auth-input-icon-left" />
                <input
                  type="tel"
                  className="auth-input"
                  placeholder={t('auth:register.phone_placeholder')}
                  name="phoneNumber"
                  value={phoneNumber}
                  onChange={onChange}
                  required
                />
              </div>
            </div>
          )}

          {/* Mot de passe avec œil */}
          <div className="auth-input-group">
            <FontAwesomeIcon icon={faLock} className="auth-input-icon-left" />
            <input
              type={showPassword ? 'text' : 'password'}
              className="auth-input has-icon-left"
              placeholder={t('auth:register.password_placeholder')}
              name="password"
              value={password}
              onChange={onChange}
              required
              minLength="6"
            />
            <button
              type="button"
              className="auth-eye-btn"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex="-1"
              aria-label={showPassword ? t('auth:register.hide_password') : t('auth:register.show_password')}
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
            </button>
          </div>

          {/* Confirmation avec œil */}
          <div className="auth-input-group">
            <FontAwesomeIcon icon={faLock} className="auth-input-icon-left" />
            <input
              type={showPassword2 ? 'text' : 'password'}
              className="auth-input has-icon-left"
              placeholder={t('auth:register.confirm_password_placeholder')}
              name="password2"
              value={password2}
              onChange={onChange}
              required
              minLength="6"
            />
            <button
              type="button"
              className="auth-eye-btn"
              onClick={() => setShowPassword2(!showPassword2)}
              tabIndex="-1"
              aria-label={showPassword2 ? t('auth:register.hide_password') : t('auth:register.show_password')}
            >
              <FontAwesomeIcon icon={showPassword2 ? faEyeSlash : faEye} />
            </button>
          </div>

          {/* 🆕 2 CASES À COCHER OBLIGATOIRES — NON PRÉ-COCHÉES */}
          <div className="auth-consents">
            <label className="auth-consent-item">
              <input
                type="checkbox"
                checked={acceptedCGU}
                onChange={(e) => setAcceptedCGU(e.target.checked)}
                aria-label="Accepter les Conditions Générales d'Utilisation"
              />
              <span>
                J'accepte les{' '}
                <button
                  type="button"
                  className="auth-consent-link"
                  onClick={(e) => { e.preventDefault(); openLegalModal('cgu'); }}
                >
                  Conditions Générales d'Utilisation
                </button>{' '}
                (incluant la certification sur l'origine licite des fonds).
              </span>
            </label>

            <label className="auth-consent-item">
              <input
                type="checkbox"
                checked={acceptedPrivacy}
                onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                aria-label="Accepter la Politique de Confidentialité"
              />
              <span>
                J'accepte la{' '}
                <button
                  type="button"
                  className="auth-consent-link"
                  onClick={(e) => { e.preventDefault(); openLegalModal('privacy'); }}
                >
                  Politique de Confidentialité
                </button>{' '}
                et de Protection des Données à caractère personnel.
              </span>
            </label>
          </div>

          <div className="auth-btn-group">
            <button
              type="submit"
              className="auth-btn auth-btn-primary"
              disabled={!isFormValid}
              style={!isFormValid ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
              {t('auth:register.submit_button')}
            </button>
          </div>

          <div className="auth-divider">{t('common:common.or')}</div>

          <button
            type="button"
            className="auth-btn auth-btn-google"
            onClick={handleGoogleLogin}
          >
            <FontAwesomeIcon icon={faGoogle} style={{ color: '#ea4335' }} />
            {t('auth:register.google_button')}
          </button>
        </form>

        <div className="auth-links">
          <div>
            {t('auth:register.have_account')}{' '}
            <Link to="/" className="auth-link">
              {t('auth:register.login_link')}
            </Link>
          </div>
        </div>
      </div>

      {/* 🆕 MODALE D'AFFICHAGE DES DOCUMENTS LÉGAUX */}
      {legalModal.open && (
        <div className="auth-legal-modal-overlay" onClick={closeLegalModal}>
          <div className="auth-legal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="auth-legal-modal-header">
              <h2>{legalModal.title}</h2>
              <button
                type="button"
                className="auth-legal-modal-close"
                onClick={closeLegalModal}
                aria-label="Fermer"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div
              className="auth-legal-modal-body"
              dangerouslySetInnerHTML={{ __html: legalModal.content }}
            />
            <div className="auth-legal-modal-footer">
              <button
                type="button"
                className="auth-btn auth-btn-primary"
                onClick={closeLegalModal}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;